import React, { useEffect, useState } from 'react';
import { useGardenerProfile } from '../hooks/useGardenerProfile';
import { supabase } from '../lib/supabase';
import { getFrostDates } from '../utils/frostDates';
import { Loader2, Calendar, CheckCircle2, User, MapPin, Sprout, Save } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import AuthGuard from '../components/auth/AuthGuard';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

type GardenType = 'raised_beds' | 'in_ground' | 'containers';
type GardenerLevel = 'brand_new' | 'beginner' | 'intermediate' | 'expert';

export default function Profile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useGardenerProfile();
  const { setItems } = useBreadcrumb();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [refreshingFrostDates, setRefreshingFrostDates] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    zipCode: '',
    level: 'beginner' as GardenerLevel,
    gardenTypes: [] as GardenType[],
    growVegetables: false,
    growFlowers: false,
    growFruits: false,
    growHerbs: false
  });

  useEffect(() => {
    setItems([
      { label: 'Home', path: '/' },
      { label: 'Profile Settings', path: '/profile' }
    ]);
  }, [setItems]);

  // Initialize form data when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        zipCode: profile.zip_code || '',
        level: profile.level,
        gardenTypes: profile.garden_type || [],
        growVegetables: profile.grow_vegetables || false,
        growFlowers: profile.grow_flowers || false,
        growFruits: profile.grow_fruits || false,
        growHerbs: profile.grow_herbs || false
      });
    }
  }, [profile]);

  // Handle success message timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleRefreshFrostDates = async () => {
    if (!profile?.zip_code) {
      setError('Please set your zip code first');
      return;
    }

    setRefreshingFrostDates(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Get new frost dates
      const dates = await getFrostDates(profile.zip_code);
      
      if (!dates) {
        throw new Error('Failed to get frost dates');
      }

      // Update profile with new dates
      const { error: updateError } = await supabase
        .from('gardener_profiles')
        .update({
          first_frost_date: dates.firstFall,
          last_frost_date: dates.lastSpring,
        })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      setSuccessMessage('Frost dates updated successfully');
    } catch (err) {
      console.error('Error refreshing frost dates:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh frost dates');
    } finally {
      setRefreshingFrostDates(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleGardenTypeToggle = (type: GardenType) => {
    setFormData(prev => {
      const newTypes = prev.gardenTypes.includes(type)
        ? prev.gardenTypes.filter(t => t !== type)
        : [...prev.gardenTypes, type];
      return { ...prev, gardenTypes: newTypes };
    });
  };

  const handleSaveProfile = async () => {
    if (!profile || !user) return;
    
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const { error: updateError } = await supabase
        .from('gardener_profiles')
        .update({
          first_name: formData.firstName,
          last_name: formData.lastName,
          zip_code: formData.zipCode,
          level: formData.level,
          garden_type: formData.gardenTypes,
          grow_vegetables: formData.growVegetables,
          grow_flowers: formData.growFlowers,
          grow_fruits: formData.growFruits,
          grow_herbs: formData.growHerbs
        })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      setSuccessMessage('Profile updated successfully');
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFrostDates = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id || !user) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Update frost dates in gardener_profiles
      const { error: updateError } = await supabase
        .from('gardener_profiles')
        .update({
          first_frost_date: firstFrostDate || null,
          last_frost_date: lastFrostDate || null
        })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      // Force recalculation of planting dates
      const { error: recalcError } = await supabase
        .rpc('recalculate_garden_dates', { 
          user_id_param: user.id 
        });

      if (recalcError) {
        console.error('Error recalculating dates:', recalcError);
        // Continue anyway, as the database trigger should handle this
      }
      
      // Show success message
      setSuccessMessage('Frost dates updated successfully');
      
    } catch (err) {
      console.error('Error saving frost dates:', err);
      setError(err instanceof Error ? err.message : 'Failed to save frost dates');
    } finally {
      setSaving(false);
    }
  };

  const [lastFrostDate, setLastFrostDate] = useState<string>('');
  const [firstFrostDate, setFirstFrostDate] = useState<string>('');

  React.useEffect(() => {
    if (profile) {
      if (profile.last_frost_date) {
        setLastFrostDate(profile.last_frost_date.split('T')[0]);
      }
      if (profile.first_frost_date) {
        setFirstFrostDate(profile.first_frost_date.split('T')[0]);
      }
    }
  }, [profile]);

  if (profileLoading) {
    return (
      <AuthGuard>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb />
        
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold">Profile Settings</h1>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
              >
                Edit Profile
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 text-green-700 p-3 rounded-md flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                <span>{successMessage}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-md flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                <span>{error}</span>
              </div>
            )}

            {/* Personal Information Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <User className="h-5 w-5 text-primary-500" />
                <h2 className="text-lg font-semibold">Personal Information</h2>
              </div>
              
              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name</label>
                    <p className="mt-1 text-gray-600">{profile?.first_name || 'Not set'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name</label>
                    <p className="mt-1 text-gray-600">{profile?.last_name || 'Not set'}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Location Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-primary-500" />
                <h2 className="text-lg font-semibold">Location</h2>
              </div>
              
              {isEditing ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                    maxLength={5}
                    pattern="[0-9]*"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Zip Code</label>
                  <p className="mt-1 text-gray-600">{profile?.zip_code || 'Not set'}</p>
                </div>
              )}
            </div>

            {/* Frost Dates Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary-500" />
                  <h2 className="text-lg font-semibold">Growing Season</h2>
                </div>
                <button
                  onClick={handleRefreshFrostDates}
                  disabled={refreshingFrostDates || !profile?.zip_code}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {refreshingFrostDates ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Calendar className="h-4 w-4" />
                      <span>Refresh Frost Dates</span>
                    </>
                  )}
                </button>
              </div>

              <form onSubmit={handleSaveFrostDates} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Spring Frost</label>
                    <input
                      type="date"
                      value={lastFrostDate}
                      onChange={(e) => setLastFrostDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500 mt-1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Fall Frost</label>
                    <input
                      type="date"
                      value={firstFrostDate}
                      onChange={(e) => setFirstFrostDate(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500 mt-1"
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    <span>Save Frost Dates</span>
                  </button>
                </div>
              </form>
            </div>

            {/* Gardening Preferences Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sprout className="h-5 w-5 text-primary-500" />
                <h2 className="text-lg font-semibold">Gardening Preferences</h2>
              </div>
              
              {isEditing ? (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Experience Level
                    </label>
                    <select
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="brand_new">Just Starting Out</option>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="expert">Advanced</option>
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Garden Type
                    </label>
                    <div className="space-y-2">
                      {[
                        { id: 'in_ground', label: 'In-Ground' },
                        { id: 'raised_beds', label: 'Raised Beds' },
                        { id: 'containers', label: 'Containers' }
                      ].map(type => (
                        <label
                          key={type.id}
                          className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={formData.gardenTypes.includes(type.id as GardenType)}
                            onChange={() => handleGardenTypeToggle(type.id as GardenType)}
                            className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                          />
                          <span>{type.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      What You Grow
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="growVegetables"
                          checked={formData.growVegetables}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                        />
                        <span>Vegetables</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="growFlowers"
                          checked={formData.growFlowers}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                        />
                        <span>Flowers</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="growFruits"
                          checked={formData.growFruits}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                        />
                        <span>Fruits</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="growHerbs"
                          checked={formData.growHerbs}
                          onChange={handleInputChange}
                          className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                        />
                        <span>Herbs</span>
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Experience Level</label>
                      <p className="mt-1 text-gray-600 capitalize">{profile?.level.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Garden Type</label>
                      <p className="mt-1 text-gray-600 capitalize">
                        {profile?.garden_type.map(type => type.replace('_', ' ')).join(', ')}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">What You Grow</label>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: 'grow_vegetables', label: 'Vegetables' },
                        { key: 'grow_flowers', label: 'Flowers' },
                        { key: 'grow_fruits', label: 'Fruits' },
                        { key: 'grow_herbs', label: 'Herbs' }
                      ].map(item => (
                        <div key={item.key}>
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              checked={profile?.[item.key as keyof typeof profile] as boolean}
                              readOnly
                              className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                            />
                            <span className="ml-2">{item.label}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Save Button */}
            {isEditing && (
              <div className="flex justify-end">
                <button
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  <span>Save Changes</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </AuthGuard>
  );
}